import { useState, useEffect, useCallback } from 'react'

export interface Shortcut {
  keys: string[]
  description: string
}

export interface ShortcutGroup {
  title: string
  shortcuts: Shortcut[]
}

export const SHORTCUTS: ShortcutGroup[] = [
  {
    title: 'Global',
    shortcuts: [
      { keys: ['?'], description: 'Open keyboard shortcuts' },
      { keys: ['Esc'], description: 'Close dialogs and panels' },
    ],
  },
  {
    title: 'Terminal',
    shortcuts: [
      { keys: ['Ctrl', '1-9'], description: 'Switch terminal tab' },
      { keys: ['Ctrl/Cmd', 'F'], description: 'Search in terminal' },
      { keys: ['Esc'], description: 'Close search / exit copy mode' },
      { keys: ['Ctrl', 'V'], description: 'Paste into terminal' },
    ],
  },
  {
    title: 'Navigation',
    shortcuts: [
      { keys: ['Ctrl/Cmd', 'K'], description: 'Focus search (where available)' },
    ],
  },
]

export function useKeyboardShortcuts() {
  const [isOpen, setIsOpen] = useState(false)

  const openShortcuts = useCallback(() => setIsOpen(true), [])
  const closeShortcuts = useCallback(() => setIsOpen(false), [])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement

      // Don't fire inside inputs, textareas, selects, or contentEditable
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable ||
        target.closest('.xterm') ||
        target.closest('[role="dialog"]')
      ) {
        return
      }

      // ? key (Shift+/ on US keyboards)
      if (e.key === '?') {
        e.preventDefault()
        setIsOpen(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  return {
    isOpen,
    openShortcuts,
    closeShortcuts,
    shortcuts: SHORTCUTS,
  }
}
